import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Button, Flex, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {useIntl, FormattedMessage} from 'react-intl'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {isHydrated} from '@salesforce/retail-react-app/app/utils/utils'
import {ChevronDownIcon, ChevronUpIcon} from '../icons'
import {useCurrentCustomer} from '../../hooks/use-current-customer'
import {useSalesAssistantSession, SA_EMAIL_KEY} from '../../hooks/use-sales-assistant-session'
import {useEndImpersonation} from '../../hooks/use-end-impersonation'
import {useAgentLogout} from '../../hooks/use-agent-logout'

const BANNER_HEIGHT_VAR = '--sa-banner-height'

const formatName = (firstName, lastName, fallback) => {
    const full = [firstName, lastName].filter(Boolean).join(' ').trim()
    return full || fallback || ''
}

function BannerDropdown({styles, roleLabel, name, items, testId}) {
    const [isOpen, setIsOpen] = useState(false)
    const closeTimer = useRef()
    const wrapperRef = useRef()

    // Hover open/close only for mouse pointers — touch devices use click only.
    const handlePointerEnter = (e) => {
        if (e.pointerType !== 'mouse') return
        clearTimeout(closeTimer.current)
        setIsOpen(true)
    }
    const handlePointerLeave = (e) => {
        if (e.pointerType !== 'mouse') return
        closeTimer.current = setTimeout(() => setIsOpen(false), 100)
    }

    // Close on outside click/tap
    useEffect(() => {
        if (!isOpen) return undefined
        const onDocPointerDown = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setIsOpen(false)
            }
        }
        document.addEventListener('pointerdown', onDocPointerDown)
        return () => document.removeEventListener('pointerdown', onDocPointerDown)
    }, [isOpen])

    return (
        <Flex
            ref={wrapperRef}
            {...styles.cluster}
            onPointerEnter={handlePointerEnter}
            onPointerLeave={handlePointerLeave}
        >
            <Text as="span" {...styles.roleLabel}>
                {roleLabel}
            </Text>
            <Button
                variant="unstyled"
                data-testid={testId}
                {...styles.trigger}
                onClick={() => setIsOpen((open) => !open)}
            >
                <Box as="span" {...styles.triggerName}>
                    {name}
                </Box>
                {isOpen ? (
                    <ChevronUpIcon {...styles.chevron} />
                ) : (
                    <ChevronDownIcon {...styles.chevron} />
                )}
            </Button>
            {isOpen && (
                <Box {...styles.menuList}>
                    {items.map((item) => (
                        <Button
                            key={item.key}
                            variant="unstyled"
                            onClick={() => {
                                setIsOpen(false)
                                item.onClick?.()
                            }}
                            {...styles.menuItem}
                        >
                            {item.label}
                        </Button>
                    ))}
                </Box>
            )}
        </Flex>
    )
}

BannerDropdown.propTypes = {
    styles: PropTypes.object,
    roleLabel: PropTypes.node,
    name: PropTypes.node,
    items: PropTypes.array,
    testId: PropTypes.string,
}

function SalesAssistantBannerInner() {
    const intl = useIntl()
    const styles = useMultiStyleConfig('SalesAssistantBanner')
    const navigate = useNavigation()
    const endImpersonation = useEndImpersonation()
    const agentLogout = useAgentLogout()
    const {
        isLoading,
        isAuthenticated,
        endlessAisleEnabled,
        salesAssistantEmail,
        salesAssistantCustomerEmail,
    } = useSalesAssistantSession()
    const {data: customer} = useCurrentCustomer()
    const containerRef = useRef()

    const isImpersonating = !!salesAssistantCustomerEmail
    // Show optimistically while the session check is in flight and hide once the
    // check reports the session inactive. Never show when the Sales Assistant flow
    // is disabled site-wide (kill-switch), even if a stale marker is present.
    const visible = endlessAisleEnabled !== false && (isLoading || isAuthenticated)

    // Keep the navbar offset in sync with the banner's real height
    // and clear it when the banner is hidden.
    useEffect(() => {
        const node = containerRef.current
        if (!node || !visible) {
            document.documentElement.style.setProperty(BANNER_HEIGHT_VAR, '0px')
            return
        }
        const setVar = () =>
            document.documentElement.style.setProperty(BANNER_HEIGHT_VAR, `${node.offsetHeight}px`)
        setVar()
        let observer
        if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(setVar)
            observer.observe(node)
        }
        return () => {
            observer && observer.disconnect()
            document.documentElement.style.setProperty(BANNER_HEIGHT_VAR, '0px')
        }
    }, [visible, isImpersonating])

    if (!visible) {
        return null
    }

    const salesAssistantName = salesAssistantEmail || ''
    const customerName = formatName(customer?.firstName, customer?.lastName)

    const goToDashboard = () => navigate('/sales-assistant/dashboard')

    const salesAssistantItems = [
        {
            key: 'dashboard',
            label: intl.formatMessage({
                id: 'sales_assistant_banner.agent.go_dashboard',
                defaultMessage: 'Go to Dashboard',
            }),
            onClick: goToDashboard,
        },
        {
            key: 'logout',
            label: intl.formatMessage({
                id: 'header.popover.action.log_out',
                defaultMessage: 'Log out',
            }),
            onClick: () => agentLogout(),
        },
    ]

    const customerItems = [
        {
            key: 'account',
            label: intl.formatMessage({
                id: 'account.accordion.button.my_account',
                defaultMessage: 'My Account',
            }),
            onClick: () => navigate('/account'),
        },
        {
            key: 'logout',
            label: intl.formatMessage({
                id: 'header.popover.action.log_out',
                defaultMessage: 'Log out',
            }),
            onClick: () => endImpersonation(),
        },
    ]

    return (
        <Flex ref={containerRef} data-testid="sales-assistant-banner" {...styles.container}>
            <Button variant="unstyled" {...styles.dashboardButton} onClick={goToDashboard}>
                <Box as="span" display={{base: 'none', lg: 'inline'}}>
                    <FormattedMessage
                        id="sales_assistant_dashboard.title"
                        defaultMessage="Sales Assistant Dashboard"
                    />
                </Box>
                <Box as="span" display={{base: 'inline', lg: 'none'}}>
                    <FormattedMessage
                        id="sales_assistant_banner.dashboard_short"
                        defaultMessage="Dashboard"
                    />
                </Box>
            </Button>

            {/* Sales Assistant */}
            <Flex {...styles.group}>
                <BannerDropdown
                    styles={styles}
                    testId="sa-banner-agent"
                    roleLabel={
                        <FormattedMessage
                            id="sales_assistant_banner.role.agent"
                            defaultMessage="Sales Assistant"
                        />
                    }
                    name={salesAssistantName}
                    items={salesAssistantItems}
                />
            </Flex>

            {/* Customer */}
            <Flex {...styles.customerRow}>
                {isImpersonating && customerName ? (
                    <BannerDropdown
                        styles={styles}
                        testId="sa-banner-customer"
                        roleLabel={
                            <FormattedMessage
                                id="sales_assistant_banner.role.customer"
                                defaultMessage="Customer"
                            />
                        }
                        name={customerName}
                        items={customerItems}
                    />
                ) : (
                    <Flex {...styles.cluster}>
                        <Text as="span" {...styles.roleLabel}>
                            <FormattedMessage
                                id="sales_assistant_banner.role.customer"
                                defaultMessage="Customer"
                            />
                        </Text>
                        <Button
                            variant="unstyled"
                            data-testid="sa-banner-search"
                            {...styles.trigger}
                            onClick={goToDashboard}
                        >
                            <Box as="span" {...styles.triggerName}>
                                <FormattedMessage
                                    id="sales_assistant_banner.customer.none"
                                    defaultMessage="Not selected (Search)"
                                />
                            </Box>
                        </Button>
                    </Flex>
                )}
            </Flex>
        </Flex>
    )
}

/**
 * Sticky banner shown while a Sales Assistant is logged in.
 */
const SalesAssistantBanner = () => {
    const [hasMarker, setHasMarker] = useState(false)

    useEffect(() => {
        const check = () => {
            try {
                setHasMarker(!!localStorage.getItem(SA_EMAIL_KEY))
            } catch {
                setHasMarker(false)
            }
        }
        check()
        // 'sa-session-started' fires after agent login (marker added);
        // 'sa-session-ended' fires after agent logout (marker removed) so the
        // banner hides immediately without a full page reload.
        window.addEventListener('sa-session-started', check)
        window.addEventListener('sa-session-ended', check)
        return () => {
            window.removeEventListener('sa-session-started', check)
            window.removeEventListener('sa-session-ended', check)
        }
    }, [])

    if (!isHydrated() || !hasMarker) {
        return null
    }

    return <SalesAssistantBannerInner />
}

SalesAssistantBanner.getTemplateName = () => 'sales-assistant-banner'

export default SalesAssistantBanner
